import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Skeleton, Link } from '@mui/material';
import { getGameHLTB } from '../services/api';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ExploreIcon from '@mui/icons-material/Explore';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Tips from './dialogs/Tips';

interface PlayTimeSectionProps {
  gameId: number;
  gameName: string | null;
  gameJapaneseName: string | null;
}

interface HLTBData {
  hltb_id: number | null;
  name: string;
  main_story_hours: number | null;
  main_story_count: number | null;
  main_plus_extras_hours: number | null;
  main_plus_extras_count: number | null;
  completionist_hours: number | null;
  completionist_count: number | null;
  all_styles_hours: number | null;
  all_styles_count: number | null;
  similarity: number | null;
}

const PlayTimeSection: React.FC<PlayTimeSectionProps> = ({ gameId, gameName, gameJapaneseName }) => {
  const [hltbData, setHltbData] = useState<HLTBData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHLTBData = async () => {
      try {
        const data = await getGameHLTB(gameId);
        setHltbData(data);
      } catch (error) {
        console.error('HLTBデータの取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHLTBData();
  }, [gameId]);

  const formatHours = (hours: number | string | null): string => {
    if (hours === null || hours === undefined || hours === 0) return '-';
    
    const numHours = typeof hours === 'string' ? parseFloat(hours) : hours;
    if (isNaN(numHours) || numHours === 0) return '-';
    
    if (numHours < 1) {
      return `${Math.round(numHours * 60)}分`;
    } else {
      return `${numHours.toFixed(1)} 時間`;
    }
  };

  const formatCount = (count: number | null): string => {
    if (count === null || count === undefined) return '-';
    if (count >= 100) return '100+';
    if (count >= 50) return '50+';
    if (count >= 10) return '10+';
    if (count >= 5) return '5+';
    return count.toString();
  };

  const hasValidPlayTime = (hours: number | string | null): boolean => {
    if (hours === null || hours === undefined) return false;
    const numHours = typeof hours === 'string' ? parseFloat(hours) : hours;
    return !isNaN(numHours) && numHours > 0;
  };

  if (loading) {
    return (
      <Box sx={{ mt: { xs: 3, sm: 3 } }}>
        <Typography variant="h2" gutterBottom sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>プレイ時間</Typography>
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
              </Grid>
      <Box sx={{ mt: 1, textAlign: 'right' }}>
        <Typography variant="caption" color="text.secondary">
          データ提供: <Link href="https://howlongtobeat.com" target="_blank" rel="noopener noreferrer" sx={{ color: 'text.secondary', textDecoration: 'underline' }}>HowLongToBeat</Link>
        </Typography>
      </Box>
    </Box>
  );
  }

  if (!hltbData || (
    !hasValidPlayTime(hltbData.main_story_hours) && 
    !hasValidPlayTime(hltbData.main_plus_extras_hours) && 
    !hasValidPlayTime(hltbData.completionist_hours)
  )) {
    return null;
  }

  return (
    <Box sx={{ mt: { xs: 2, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography variant="h2" gutterBottom sx={{ mb: 0, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>プレイ時間</Typography>
        <Tips
          title="プレイ時間について"
          content={
            <Box>
              <Typography variant="body2" paragraph>
                <strong>メイン</strong>: メインストーリーや主目的をクリアするのに必要な時間。エンディング。
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>メイン + サブ</strong>: メインストーリーに加えて主要なサブクエストやサイドコンテンツをクリアするのに必要な時間
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>コンプリート</strong>: ゲーム内の全ての要素をクリアするのに必要な時間。必ずしも実績やトロフィーの全解除そのものを定義する指標ではない。
              </Typography>
              <Typography variant="caption" sx={{ mt: 2, pt: 0.5, borderTop: '1px solid rgba(255, 255, 255, 0.1)', color: 'text.secondary', display: 'block',fontWeight: 400}}>
                Data from <Link href={`https://howlongtobeat.com/game/${hltbData?.hltb_id}`} target="_blank" rel="noopener noreferrer" sx={{ color: 'text.secondary' }}>howlongtobeat.com</Link>
              </Typography>
            </Box>
          }
        />
      </Box>
      <Grid container spacing={1}>
        {hltbData.main_story_hours && (
          <Grid item xs={4}>
            <Box sx={{ 
              p: { xs: 1, sm: 1.5 }, 
              borderRadius: 2,
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <PlayCircleOutlineIcon sx={{ color: 'text.secondary', fontSize: { xs: '0.8rem', sm: '1rem' } }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1 }}>
                  メイン
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: '600', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                {formatHours(hltbData.main_story_hours)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonOutlineIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: 'pulse.info' }} />
                <Typography variant="caption" color="pulse.info" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                  {formatCount(hltbData.main_story_count)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}
        
        {hltbData.main_plus_extras_hours && (
          <Grid item xs={4}>
            <Box sx={{ 
              p: { xs: 1, sm: 1.5 }, 
              borderRadius: 2,
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <ExploreIcon sx={{ color: 'text.secondary', fontSize: { xs: '0.8rem', sm: '1rem' } }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1 }}>
                  {window.innerWidth < 600 ? 'メイン+サブ' : 'メイン + サブ'}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: '600', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                {formatHours(hltbData.main_plus_extras_hours)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonOutlineIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: 'pulse.info' }} />
                <Typography variant="caption" color="pulse.info" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                  {formatCount(hltbData.main_plus_extras_count)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}
        
        {hltbData.completionist_hours && (
          <Grid item xs={4}>
            <Box sx={{ 
              p: { xs: 1, sm: 1.5 }, 
              borderRadius: 2,
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <EmojiEventsIcon sx={{ color: 'text.secondary', fontSize: { xs: '0.8rem', sm: '1rem' } }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' }, lineHeight: 1 }}>
                  コンプリート
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: '600', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                {formatHours(hltbData.completionist_hours)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonOutlineIcon sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: 'pulse.info' }} />
                <Typography variant="caption" color="pulse.info" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                  {formatCount(hltbData.completionist_count)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
      <Typography variant="caption" color="pulse.info" sx={{ mt: 1, display: 'block',lineHeight: 1.2 }}>
        『{gameJapaneseName || gameName || hltbData?.name}』をクリアするための通常プレイにおける所要時間は約{formatHours(hltbData?.main_story_hours)}程度です。
        コンプリートを目指すと約{formatHours(hltbData?.completionist_hours)}程度が必要になります。
      </Typography>
    </Box>
  );
};

export default PlayTimeSection; 